import React, { useState } from 'react';
import { AgentDetailData } from '../types/index';
import { Globe, Github, FileText, Twitter, MessageCircle, Copy, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import {
    formatMarketCap,
    getTokenUtilityLevel,
    getAutonomyLevelColor,
    getAutonomyBarHeight
} from '../utils/format';

interface Props {
    agent: AgentDetailData;
}

interface ParsedContractAddress {
    chain: string;
    address: string;
}

const AgentDetailHeader = ({ agent }: Props) => {
    const [showAllAddresses, setShowAllAddresses] = useState(false);
    const [copiedAddress, setCopiedAddress] = useState<string | null>(null);

    const tokenUtilityLevel = getTokenUtilityLevel(agent.token_utility);

    const parseContractAddresses = (): ParsedContractAddress[] => {
        if (!agent.contract_addresses) return [];
        return agent.contract_addresses.map((addr: string) => {
            try {
                return JSON.parse(addr) as ParsedContractAddress;
            } catch (e) {
                console.error('Error parsing contract address:', e);
                return null;
            }
        }).filter((addr): addr is ParsedContractAddress => addr !== null);
    };

    const getChainExplorerUrl = (chain: string, address: string): string => {
        const chainMap: { [key: string]: string } = {
            'Base': 'https://basescan.org/address/',
            'Ethereum': 'https://etherscan.io/address/',
            'Arbitrum': 'https://arbiscan.io/address/',
            'Polygon': 'https://polygonscan.com/address/',
            'Solana': 'https://solscan.io/address/',
        };
        return chainMap[chain] ? `${chainMap[chain]}${address}` : '#';
    };

    const handleCopyAddress = (address: string): void => {
        navigator.clipboard.writeText(address);
        setCopiedAddress(address);
        setTimeout(() => setCopiedAddress(null), 2000);
    };

    const parsedAddresses = parseContractAddresses();
    const mainAddress = parsedAddresses[0];

    return (
        <div className="mt-6 sm:mt-8 lg:mt-16 mb-6 sm:mb-8 lg:mb-12 z-[1] relative">
            {/* Main Container */}
            <div className="flex flex-col space-y-6 sm:space-y-8 lg:space-y-10">
                {/* Header Section */}
                <div className="flex items-center gap-4">
                    {/* Agent Icon */}
                    {agent.icon_url ? (
                        <div className="relative">
                            <img 
                                src={agent.icon_url} 
                                alt={`${agent.name} icon`}
                                className="w-16 h-16 rounded-2xl object-cover bg-gray-800/50 border border-gray-700/50"
                                onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="%236b7280" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"%3E%3Crect width="18" height="18" x="3" y="3" rx="2" ry="2"%3E%3C/rect%3E%3Cpath d="M12 8v8"%3E%3C/path%3E%3Cpath d="M8 12h8"%3E%3C/path%3E%3C/svg%3E';
                                    target.className = "w-16 h-16 rounded-2xl p-4 bg-gray-800/50 border border-gray-700/50";
                                }}
                            />
                        </div>
                    ) : (
                        <div className="w-16 h-16 rounded-2xl bg-gray-800/50 border border-gray-700/50 flex items-center justify-center">
                            <span className="text-gray-400 text-2xl">♦</span>
                        </div>
                    )}
                    <div className="flex flex-col gap-2">
                        <h1 className="text-xl sm:text-2xl font-semibold text-white">{agent.name}</h1>
                        <div className="flex flex-wrap items-center gap-2">
                            {agent.agent_symbol && parsedAddresses.map((address, index) => (
                                <div key={index} className="flex items-center gap-2">
                                    <a
                                        href={getChainExplorerUrl(address.chain, address.address)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-400 hover:text-blue-300 transition-colors text-lg"
                                    >
                                        {`${agent.agent_symbol} (${address.chain})`}
                                    </a>
                                    <button
                                        onClick={() => handleCopyAddress(address.address)}
                                        className="p-1 hover:bg-gray-700/50 rounded-md transition-colors"
                                        title="Copy contract address"
                                    >
                                        <Copy className={`w-4 h-4 ${copiedAddress === address.address ? 'text-green-500' : 'text-gray-400'}`} />
                                    </button>
                                </div>
                            ))}
                        </div>

                        {/* Tags with chain info */}
                        <div className="flex flex-wrap gap-2 mt-2">
                            {/* Team Status Tag */}
                            <span
                                className={`px-2.5 py-1 text-xs rounded-md ${
                                    agent.agent_team_status === 'DOXXED'
                                        ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                                        : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                                }`}
                            >
                                {agent.agent_team_status === 'DOXXED' ? 'Doxxed' : 'Anon'}
                            </span>

                            {/* Chain Tags */}
                            {parsedAddresses.map((address, index) => (
                                <span 
                                    key={index}
                                    className="px-2.5 py-1 text-xs bg-gray-800/50 text-gray-300 rounded-md border border-gray-700/30"
                                >
                                    {address.chain}
                                </span>
                            ))}

                            {/* Other tags */}
                            {agent.tags?.map((tag, index) => (
                                <span
                                    key={index}
                                    className="px-2.5 py-1 text-xs bg-gray-800/50 text-gray-300 rounded-md"
                                >
                                    {tag}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Content Section */}
                <div className="flex flex-col lg:flex-row justify-between items-start gap-6 lg:gap-8">
                    <div className="flex flex-col w-full lg:flex-1">
                        {/* Links Row */}
                        <div className="flex flex-wrap gap-4 sm:gap-6 mb-6 sm:mb-8">
                            {agent.website_url && (
                                <a
                                    href={agent.website_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
                                >
                                    <Globe className="w-4 h-4" />
                                    <span className="text-sm">Website</span>
                                </a>
                            )}
                            {agent.github_url && (
                                <a
                                    href={agent.github_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
                                >
                                    <Github className="w-4 h-4" />
                                    <span className="text-sm">Github</span>
                                </a>
                            )}
                            {agent.documentation_url && (
                                <a
                                    href={agent.documentation_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
                                >
                                    <FileText className="w-4 h-4" />
                                    <span className="text-sm">Docs</span>
                                </a>
                            )}
                            {agent.x_url && (
                                <a
                                    href={agent.x_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
                                >
                                    <Twitter className="w-4 h-4" />
                                    <span className="text-sm">Twitter</span>
                                </a>
                            )}
                            {agent.discord_url && (
                                <a
                                    href={agent.discord_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
                                >
                                    <MessageCircle className="w-4 h-4" />
                                    <span className="text-sm">Discord</span>
                                </a>
                            )}
                        </div>

                        {/* Stats Card */}
                        <div className="w-full">
                            <div className="bg-[#1A1721]/50 rounded-xl p-4 sm:p-6">
                                <h2 className="text-lg font-semibold mb-4 sm:mb-6">Agent Stats</h2>
                                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
                                    <div>
                                        <div className="text-gray-400 text-sm mb-2 sm:mb-3">Market Cap</div>
                                        <div className="text-lg sm:text-xl font-medium">
                                            {formatMarketCap(agent.marketCap)}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-gray-400 text-sm mb-2 sm:mb-4">Token Utility</div>
                                        <div className="flex flex-col gap-2">
                                            <div className="flex items-center gap-2">
                                                <div className="flex gap-1">
                                                    {[...Array(5)].map((_, i) => (
                                                        <div
                                                            key={i}
                                                            className={`w-1.5 sm:w-2 h-4 sm:h-6 rounded-sm ${i < tokenUtilityLevel
                                                                    ? 'bg-green-500'
                                                                    : 'bg-gray-700'
                                                                }`}
                                                        />
                                                    ))}
                                                </div>
                                                <span className="text-gray-400 text-xs sm:text-sm">
                                                    Level {tokenUtilityLevel}/5
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-gray-400 text-sm mb-2 sm:mb-3">Framework</div>
                                        <div className="text-lg sm:text-xl font-medium">{agent.framework}</div>
                                    </div>
                                    <div>
                                        <div className="text-gray-400 text-sm mb-2 sm:mb-3">Source Status</div>
                                        <div className="text-lg sm:text-xl font-medium">
                                            {agent.openSource ? 'Open Source' : 'Closed'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Autonomy Level Chart */}
                    <div className="w-full lg:w-[400px] mt-6 lg:mt-0">
                        <div className="flex items-end justify-evenly h-[120px] sm:h-[140px] lg:h-[160px] mb-2">
                            {[1, 2, 3, 4].map((barLevel) => (
                                <div key={barLevel} className="w-8 sm:w-10">
                                    <div
                                        className={`w-full ${getAutonomyLevelColor(agent.level, barLevel)} 
                                            rounded-lg transition-all ${getAutonomyBarHeight(barLevel)}`}
                                    />
                                </div>
                            ))}
                        </div>
                        <div className="flex justify-evenly text-center">
                            {['Low', 'Medium', 'High', 'Full'].map((label, index) => (
                                <span
                                    key={label}
                                    className={`text-xs sm:text-sm items-center ${agent.level === index + 1
                                            ? 'text-white text-center'
                                            : 'text-gray-600 text-center'
                                        }`}
                                >
                                    {label}
                                </span>
                            ))}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-400 text-center mt-4">
                            Level of Autonomy
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AgentDetailHeader; 